"use client"

import Link from "next/link"
import { Calendar, MapPin, Users } from "lucide-react"
import type { EventCardStatusPresentation } from "@/features/events/event-card"
import { EventCardThumbnail } from "@/components/media/gallery-media-image"
import { cn } from "@/lib/utils"

export type { EventCardStatusPresentation }

export interface EventCardProps {
  href: string
  title: string
  image: string
  location: string
  attendeesCount?: number
  /** Label after count, e.g. "going" vs "attending" */
  attendeesCaption?: string
  variant?: "default" | "compact"
  showStatus?: boolean
  statusPresentation?: EventCardStatusPresentation | null
  /** When set (default variant), shows the two-line date header above the card */
  dateHeader?: { primary: string; secondary: string } | null
  /** Shown next to calendar icon; compact variant typically uses a short string */
  dateLine: string
  index?: number
  /** Landing / first-row LCP thumb */
  coverPriority?: boolean
  /** Optional surface polish (e.g. landing preview) — does not affect default list cards. */
  surfaceClassName?: string
  /** Semantic title element for heading-order control across contexts. */
  titleAs?: "h2" | "h3" | "p"
}

export function EventCard({
  href,
  title,
  image,
  location,
  attendeesCount,
  attendeesCaption = "attending",
  index = 0,
  showStatus = false,
  variant = "default",
  statusPresentation = null,
  dateHeader = null,
  dateLine,
  coverPriority = false,
  surfaceClassName,
  titleAs = "h3",
}: EventCardProps) {
  const StatusIcon = statusPresentation?.Icon
  const TitleTag = titleAs

  return (
    <Link
      href={href}
      className="group block w-full min-w-0 max-w-full"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className="animate-in fill-mode-both slide-in-from-bottom-2 duration-500 fade-in min-w-0 max-w-full">
        {variant === "default" && dateHeader && (
          <div className="event-card-date-header">
            <span className="type-moment-day-marker">{dateHeader.primary}</span>
            <span className="type-caption text-[color-mix(in_oklab,var(--text-muted)_74%,transparent)]">
              {dateHeader.secondary}
            </span>
          </div>
        )}

        <div
          className={cn(
            "relative w-full max-w-full overflow-hidden rounded-card-shell border border-border-subtle/90 bg-surface-muted/40",
            "transition-[border-color,background-color,box-shadow] duration-300",
            "hover:border-border-default/70 hover:bg-surface-muted/55 hover:shadow-[var(--shadow-1)]",
            variant === "compact"
              ? "event-card-shell-pad-compact"
              : "event-card-shell-pad-default",
            surfaceClassName,
          )}
        >
          <div
            className={cn(
              "event-card-inner-row w-full min-w-0 max-w-full",
              variant === "compact" ? "" : "event-card-inner-pad",
            )}
          >
            <div
              className={cn(
                "relative flex-shrink-0 overflow-hidden rounded-thumb-editorial",
                variant === "compact" ? "w-14 h-14" : "w-16 h-16 sm:w-20 sm:h-20",
              )}
            >
              {/* High-fidelity row thumb: `image` prop must be viewer/full URL (see eventCoverSrcThumbnail). */}
              <EventCardThumbnail
                src={image}
                alt={title}
                priority={coverPriority}
                sizes={
                  variant === "compact"
                    ? "(max-width: 640px) 3.5rem, 3.5rem"
                    : "(max-width: 640px) 4rem, 5rem"
                }
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent" />
            </div>

            <div className="min-w-0 flex-1">
              <div className="event-card-title-row">
                <TitleTag className="type-card-title line-clamp-2 sm:line-clamp-1 transition-colors group-hover:text-[color-mix(in_oklab,var(--text-primary)_82%,transparent)]">
                  {title}
                </TitleTag>
                {showStatus && statusPresentation && StatusIcon && (
                  <span
                    className={cn(
                      "type-chip flex flex-shrink-0 items-center rounded-full border px-[var(--space-xs)] py-[var(--space-2xs)]",
                      "gap-[var(--space-xs)]",
                      statusPresentation.className,
                    )}
                  >
                    <StatusIcon className="w-3 h-3" />
                    <span className="hidden sm:inline">{statusPresentation.label}</span>
                  </span>
                )}
              </div>
              <div className="event-card-meta-row type-caption min-w-0 flex-col items-start sm:flex-row sm:items-center">
                <span className="event-card-meta-item min-w-0 max-w-full">
                  <Calendar className="w-3.5 h-3.5" />
                  {dateLine}
                </span>
                <span className="event-card-meta-item min-w-0 max-w-full">
                  <MapPin className="w-3.5 h-3.5" />
                  <span className="max-w-full break-words sm:truncate sm:max-w-none">
                    {location}
                  </span>
                </span>
                {attendeesCount !== undefined && (
                  <span className="event-card-meta-item hidden sm:flex">
                    <Users className="w-3.5 h-3.5" />
                    {attendeesCount} {attendeesCaption}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}
